import React, { useState, useEffect, useCallback } from "react"
import { Plus, Pencil, Trash2, MessageSquareText, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Skeleton } from "src/components/ui/skeleton"
import { useToast } from "src/components/ui/toast"

import { useTemplates, useDeleteTemplate } from "src/features/templates/hooks/useTemplates"
import { TemplateDrawer } from "src/features/templates/components/TemplateDrawer"
import { CHANNELS, CATEGORY_MAP, STATUS_MAP } from "src/features/templates/constants"

function ChannelTab({ channel, active, onClick }) {
  const Icon = channel.icon
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 border-b-2 px-1 py-3 text-[14px] font-medium transition-colors ${active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
        }`}
    >
      <Icon className="h-4 w-4" />
      {channel.label}
    </button>
  )
}

export default function TemplatesPage() {
  const { toast } = useToast()
  const [channel, setChannel] = useState("whatsapp")
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState(null)

  const { data, loading, fetch } = useTemplates()
  const deleteHook = useDeleteTemplate()

  const [sortField, setSortField] = useState(null)
  const [sortDirection, setSortDirection] = useState(null)

  const handleSort = (field) => {
    if (sortField === field) {
      if (sortDirection === "asc") setSortDirection("desc")
      else if (sortDirection === "desc") {
        setSortField(null)
        setSortDirection(null)
      }
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const reload = useCallback(() => fetch({ channel }), [fetch, channel])
  useEffect(() => { reload() }, [reload])

  const templates = data?.templates ?? []

  const sortedTemplates = React.useMemo(() => {
    if (!sortField || !sortDirection) return templates
    return [...templates].sort((a, b) => {
      let aVal, bVal
      if (sortField === "name") {
        aVal = a.name || ""
        bVal = b.name || ""
      } else if (sortField === "about") {
        aVal = CATEGORY_MAP[a.category]?.label || a.category || ""
        bVal = CATEGORY_MAP[b.category]?.label || b.category || ""
      } else if (sortField === "status") {
        aVal = STATUS_MAP[a.status]?.label || a.status || ""
        bVal = STATUS_MAP[b.status]?.label || b.status || ""
      } else if (sortField === "body") {
        aVal = a.body || ""
        bVal = b.body || ""
      }

      if (typeof aVal === "string") {
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal)
      } else {
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1
        return 0
      }
    })
  }, [templates, sortField, sortDirection])

  const renderSortableHeader = (label, field, className = "") => {
    const isSorted = sortField === field
    return (
      <button
        onClick={() => handleSort(field)}
        className={`inline-flex items-center gap-1 hover:text-black focus:outline-none font-semibold text-black ${className}`}
      >
        <span>{label}</span>
        {isSorted ? (
          sortDirection === "asc" ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-60 hover:opacity-100 shrink-0" />
        )}
      </button>
    )
  }

  function openCreate() { setEditing(null); setDrawerOpen(true) }
  function openEdit(t) { setEditing(t); setDrawerOpen(true) }

  async function handleDelete(t) {
    if (!window.confirm(`Delete template "${t.name}"?`)) return
    try {
      await deleteHook.mutate(t.id)
      toast({ title: "Template deleted", description: t.name })
      reload()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not delete template"
      toast({ variant: "destructive", title: "Error", description: msg })
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[17px] font-bold text-slate-900">Templates</h2>
          <p className="text-[13px] text-slate-500">Reusable WhatsApp, SMS and email messages with variables.</p>
        </div>
        <Button size="sm" className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          New Template
        </Button>
      </div>

      <nav className="flex items-center gap-6 border-b border-slate-200">
        {CHANNELS.map((c) => (
          <ChannelTab key={c.value} channel={c} active={channel === c.value} onClick={() => setChannel(c.value)} />
        ))}
      </nav>

      {loading && !data ? (
        <Skeleton className="h-48 w-full rounded-xl" />
      ) : templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-16 text-center">
          <MessageSquareText className="h-10 w-10 text-slate-300" />
          <div>
            <p className="font-medium text-slate-700">No templates yet</p>
            <p className="text-[13px] text-slate-500">Create your first {channel} template.</p>
          </div>
          <Button size="sm" className="gap-2" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-[50px]">#</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Name", "name")}</TableHead>
                <TableHead className="text-black">{renderSortableHeader("About", "about")}</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Status", "status")}</TableHead>
                <TableHead className="text-black">{renderSortableHeader("Preview", "body")}</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedTemplates.map((t, idx) => {
                const status = STATUS_MAP[t.status]
                return (
                  <TableRow key={t.id} className="group">
                    <TableCell className="text-xs text-slate-400">{idx + 1}</TableCell>
                    <TableCell className="font-medium text-slate-900">{t.name}</TableCell>
                    <TableCell className="text-slate-500">{CATEGORY_MAP[t.category]?.label || t.category}</TableCell>
                    <TableCell>
                      {status && <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-medium ${status.badge}`}>{status.label}</span>}
                    </TableCell>
                    <TableCell className="max-w-[280px] truncate text-[12px] text-slate-400">{t.body}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-0.5">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)} title="Edit">
                          <Pencil className="h-3.5 w-3.5 text-slate-400 hover:text-slate-700" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(t)} title="Delete">
                          <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <TemplateDrawer
        open={drawerOpen}
        template={editing}
        channel={channel}
        onClose={() => setDrawerOpen(false)}
        onSuccess={reload}
      />
    </div>
  )
}


